/**
 * Seeded RNG + hashing helpers.
 *
 * Determinism contract: the same seed must reproduce the same sequence across
 * runs, devices, and Node/browsers. We use mulberry32 — a fast 32-bit PRNG
 * with a full 2^32 period and good statistical quality, implemented in pure
 * arithmetic with no platform dependencies.
 */

/** A function that returns a uniformly-distributed float in [0, 1). */
export type Rng = () => number;

/**
 * Create a seeded RNG from a 32-bit integer.
 *
 * @param seed non-negative integer up to 2^32 - 1.
 * @returns a function returning floats in [0, 1).
 */
export function mulberry32(seed: number): Rng {
  // Force into unsigned 32-bit range; bit ops below keep state masked.
  let a = seed >>> 0;

  return function rng(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Hash a string into an unsigned 32-bit integer (FNV-1a variant).
 *
 * Stable across platforms; used to derive seeds from run identifiers or to
 * hash sim state for determinism checks.
 */
export function hashString(str: string): number {
  let h = 0x811c9dc5; // FNV offset basis (2166136261)
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    // FNV prime (16777619) via Math.imul to stay in 32-bit range.
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Hash a sequence of numbers into an unsigned 32-bit integer.
 *
 * Useful for summarizing sim state (positions, velocities) into a single
 * value for determinism assertions in headless tests.
 */
export function hashNumbers(values: Iterable<number>): number {
  let h = 0x811c9dc5;
  for (const v of values) {
    // Mix the 32-bit reinterpretation of the float's bits.
    const buf = new ArrayBuffer(4);
    new Float32Array(buf)[0] = v;
    const bits = new Uint32Array(buf)[0] >>> 0;
    h ^= bits & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (bits >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (bits >>> 16) & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (bits >>> 24) & 0xff;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
